import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { PatientFormGroup } from '../../../shared/patient-form/patient-form.model';

export interface FlaggedPatientRow {
  rowIndex: number;
  patientFormGroup: PatientFormGroup;
}

// A control blocks the import when it is ENABLED and invalid with a non-empty value —
// invalid-but-empty controls are tolerated because empty optional fields are cleared to null on
// save, and DISABLED controls are excluded because toApiInput() disables every successfully
// created row (a created patient must never re-flag as blocking). Shared by the patients
// FormArray validator, the flaggedPatientRows getter, and the flagged-row review list so they
// can never disagree about what actually blocks the "Next" button.
export function isBlockingInvalidControl(patientControl: AbstractControl): boolean {
  return patientControl.enabled && !patientControl.valid && patientControl.value !== '';
}

// Names of the controls that currently block this row from importing. Used by the flagged-row
// review to tell the user exactly which fields need fixing.
export function getBlockingInvalidControlKeys(patientFormGroup: PatientFormGroup): string[] {
  return Object.keys(patientFormGroup.controls).filter(controlKey =>
    isBlockingInvalidControl(patientFormGroup.controls[controlKey])
  );
}

function hasBlockingInvalidControl(patientFormGroup: PatientFormGroup): boolean {
  return Object.keys(patientFormGroup.controls).some(controlKey =>
    isBlockingInvalidControl(patientFormGroup.controls[controlKey])
  );
}

export class CSVPatientsFormGroup extends FormGroup {
  controls: {
    patients: FormArray;
    doesPatientExist: FormControl;
    shouldCreateDuplicates: FormControl;
  };

  constructor() {
    super({
      patients: new FormArray(
        [],
        [
          Validators.minLength(1),
          (patients: FormArray) => {
            return patients.controls.every(
              (patientFormGroup: PatientFormGroup) =>
                !hasBlockingInvalidControl(patientFormGroup)
            )
              ? null
              : { invalidColumns: '' };
          }
        ]
      ),
      doesPatientExist: new FormControl(undefined, Validators.required),
      shouldCreateDuplicates: new FormControl(true)
    });
  }

  reset() {
    super.reset();
    this.controls.patients.controls = [];
    this.controls.shouldCreateDuplicates.setValue(false);
  }

  // 1-based offset added when reporting CSV row numbers to the user: 2 when the file has a
  // header row (data row 0 sits on spreadsheet row 2), 1 otherwise. Set by the CSV-preview step.
  public csvRowNumberOffset = 2;

  // Every row (with its original CSV data-row index) that currently blocks the import. Iterates
  // the FULL patients FormArray — not the 100-row preview — so flagged rows in large imports are
  // covered. Single pass so allocations scale with the flagged count, not the file size.
  public get flaggedPatientRows(): FlaggedPatientRow[] {
    const flaggedRows: FlaggedPatientRow[] = [];
    (this.controls.patients.controls as PatientFormGroup[]).forEach(
      (patientFormGroup, positionalIndex) => {
        if (hasBlockingInvalidControl(patientFormGroup)) {
          flaggedRows.push({
            rowIndex:
              patientFormGroup.csvImportRowIndex != null
                ? patientFormGroup.csvImportRowIndex
                : positionalIndex,
            patientFormGroup
          });
        }
      }
    );
    return flaggedRows;
  }

  public get hasExistingPatients(): boolean {
    const possibleDuplicates = this.controls.doesPatientExist.value as boolean[];
    return (
      possibleDuplicates && possibleDuplicates.length !== 0 && possibleDuplicates.some(val => val)
    );
  }

  // Row PatientFormGroups are appended straight into patients.controls (bulk-import performance),
  // so they have no parent link and silent leaf writes leave each row group's cached value and
  // status stale. Recompute every row group FIRST so the FormArray below aggregates fresh child
  // state — otherwise the array stays INVALID (and keeps stale junk in .value) after skips.
  public updateThisAndDescendants(opts?: { emitEvent?: boolean; onlySelf?: boolean }) {
    (this.controls.patients.controls as PatientFormGroup[]).forEach(patientFormGroup =>
      patientFormGroup.updateValueAndValidity({ ...(opts || {}), onlySelf: true })
    );
    this.controls.patients.updateValueAndValidity(opts);
    this.controls.doesPatientExist.updateValueAndValidity(opts);
    super.updateValueAndValidity(opts);
  }

  public get patientsToCreate(): PatientFormGroup[] {
    return (this.controls.shouldCreateDuplicates.value
      ? this.controls.patients.controls
      : this.controls.patients.controls.filter(
          (patient, index) =>
            !this.controls.doesPatientExist.value || !this.controls.doesPatientExist.value[index]
        )) as PatientFormGroup[];
  }
}
